import { Link } from 'react-router-dom';
import { useSoc, type SocIncident } from '@/api/soc';
import { CompanyCell, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { HuntressLink, INDICATOR_LABEL, IncidentStatusChip, SeverityChip, TicketLinks } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { socIncidentPath } from '@/lib/routes';

/** Huntress incident reports (EDR + ITDR), newest first, with the Autotask ticket Huntress raised. */

const DAY = 86_400_000;
const within = (i: SocIncident, days: number) => !!i.sentAt && Date.now() - new Date(i.sentAt).getTime() < days * DAY;
const FILTERS: FilterDef<SocIncident>[] = [
  { id: 'all',     label: 'All',                 test: () => true },
  { id: 'open',    label: 'Open',                test: (i) => i.status !== 'closed' },
  { id: 'recent',  label: 'Last 30 days',        test: (i) => within(i, 30) },
  { id: 'serious', label: 'Critical / high · 90d', test: (i) => /critical|high/i.test(i.severity ?? '') && within(i, 90) },
  { id: 'identity', label: 'Identity (ITDR)',    test: (i) => i.platform === 'microsoft_365' },
  { id: 'no-ticket', label: 'No Autotask ticket', test: (i) => i.ticketIds.length === 0 },
];

export default function SocIncidentsPage() {
  const { data, isLoading, error, isFetching } = useSoc({ scoped: true });
  const items = data?.configured ? data.incidents : [];
  const l = useListState('cosmos:soc:incidents:show', FILTERS, items,
    (i) => `${i.subject} ${i.summary ?? ''} ${i.hostname ?? ''} ${i.orgName} ${i.companyName ?? ''} ${i.ticketIds.join(' ')}`);

  return (
    <ListPage area="SOC" title="Incidents" count={data?.configured ? l.rows.length : null} isFetching={isFetching}
      subtitle={data?.configured && <>Huntress incident reports · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Host, client, summary, ticket' }}
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={data?.configured} vendor="Huntress" />
      {data?.configured && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1100 }}>
            <thead className={theadCls}>
              <tr><Th>Sent</Th><Th>Severity</Th><Th>Incident</Th><Th>Client</Th><Th>Indicators</Th><Th>Status</Th><Th>Ticket</Th><Th /></tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {l.rows.length === 0 && <Empty cols={8} />}
              {l.rows.map((i) => (
                <tr key={i.id} className="hover:bg-muted/30">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground" title={i.sentAt ?? ''}>{fmtRelative(i.sentAt)}</td>
                  <td className="px-3 py-2"><SeverityChip severity={i.severity} /></td>
                  <td className="max-w-[380px] px-3 py-2">
                    <Link to={socIncidentPath(i.id)} className="block truncate font-medium text-foreground hover:underline" title={i.summary ?? i.subject}>{i.summary ?? i.subject}</Link>
                    <div className="truncate text-[11px] text-muted-foreground">{i.hostname ?? (i.platform === 'microsoft_365' ? 'Microsoft 365 identity' : '—')}</div>
                  </td>
                  <td className="max-w-[200px] px-3 py-2 text-xs"><CompanyCell companyId={i.companyId} companyName={i.companyName} fallback={i.orgName} /></td>
                  <td className="min-w-[150px] px-3 py-2 text-xs text-muted-foreground">{i.indicatorTypes.map((t) => INDICATOR_LABEL[t] ?? t).join(', ') || '—'}</td>
                  <td className="px-3 py-2"><IncidentStatusChip status={i.status} /></td>
                  <td className="whitespace-nowrap px-3 py-2"><TicketLinks ids={i.ticketIds} /></td>
                  <td className="px-3 py-2 text-right"><HuntressLink href={i.portalUrl} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
